import type { Seat } from '@campfire/engine';
import { PEGS, type GuessRecord, type MastermindView, type RoundRecord } from '@campfire/game-mastermind';
import { h } from '../dom.ts';
import { name, type GameUi, type TableCtx } from '../games.ts';

const peg = (c: number | null, testid?: string, extra = '') =>
  c === null
    ? h('span.mm-peg.empty', testid ? { 'data-testid': testid } : {})
    : h(`span.mm-peg.c${c}${extra}`, { 'data-testid': testid, 'data-color': c, title: PEGS[c]?.name ?? '' }, h('span.mm-letter', {}, PEGS[c]?.letter ?? '?'));

const keys = (g: { black: number; white: number }, len: number) =>
  h(
    'span.mm-keys',
    { 'aria-label': `${g.black} right place, ${g.white} right color wrong place`, 'data-black': g.black, 'data-white': g.white },
    Array.from({ length: len }, (_, i) => h(`span.mm-key${i < g.black ? '.black' : i < g.black + g.white ? '.white' : ''}`)),
  );

const row = (ctx: TableCtx, g: GuessRecord, len: number, i: number, solo: boolean) =>
  h(
    'div.mm-row',
    { 'data-testid': `mm-guess-${i}` },
    h('span.mm-num', {}, i + 1),
    h('span.mm-pegs', {}, g.guess.map((c) => peg(c))),
    keys(g, len),
    solo ? null : h('span.mm-by', {}, name(ctx, g.seat)),
  );

const revealRow = (ctx: TableCtx, r: RoundRecord, n: number) =>
  h(
    'div.mm-reveal',
    { 'data-testid': 'mm-reveal' },
    h('div.muted', {}, `${r.codemaker === null ? 'The code' : `${name(ctx, r.codemaker)} set the code`} (round ${n}):`),
    h('div.mm-pegs', { 'data-testid': 'mm-revealed' }, r.secret.map((c) => peg(c))),
    h(
      'div',
      { 'data-testid': 'mm-reveal-text' },
      r.broken ? `Cracked in ${r.guesses.length} guess${r.guesses.length === 1 ? '' : 'es'}.` : 'Not cracked.',
      r.scorer !== null ? ` ${name(ctx, r.scorer)} earn${r.scorer === ctx.you ? '' : 's'} ${r.points} point${r.points === 1 ? '' : 's'}.` : '',
    ),
  );

export const mastermindUi: GameUi = {
  id: 'mastermind',
  privateHands: true,
  seatInfo: (ctx, s) => ((ctx.msg.view as MastermindView).rounds > 1 ? `${(ctx.msg.view as MastermindView).scores[s] ?? 0} pts` : ''),
  render(ctx) {
    const v = ctx.msg.view as MastermindView;
    const len = v.options.length;
    const solo = v.rounds === 1;
    const done = v.phase === 'over';
    const setting = v.phase === 'set';
    const sig = `${v.round}-${v.phase}-${v.guesses.length}`;
    if (ctx.local.get<string>('mm-sig') !== sig) {
      ctx.local.set('mm-sig', sig);
      ctx.local.set('mm-cur', []);
    }
    const cur = ctx.local.get<number[]>('mm-cur') ?? [];
    const set = (next: number[]) => ctx.local.set('mm-cur', next);
    const mine = ctx.myTurn && !done;

    const status = done
      ? 'Game over.'
      : setting
        ? mine
          ? 'Build your secret code, then lock it in. Look away, everyone else.'
          : `${v.codemaker === null ? 'The app' : name(ctx, v.codemaker)} ${v.codemaker === ctx.you ? 'are' : 'is'} setting the code…`
        : mine
          ? `${solo ? 'Crack the code.' : 'Your guess.'} ${v.options.guesses - v.guesses.length} guess${v.options.guesses - v.guesses.length === 1 ? '' : 'es'} left.`
          : `${v.turn === null ? '' : name(ctx, v.turn)} ${v.turn === ctx.you ? 'guess' : 'guesses'} next.`;

    const board = h(
      'div.mm-board',
      { 'data-testid': 'mm-board' },
      v.guesses.map((g, i) => row(ctx, g, len, i, solo)),
    );

    const editor = mine
      ? h(
          'div.mm-editor',
          { 'data-testid': 'mm-editor' },
          h('div.mm-pegs.mm-current', { 'data-testid': 'mm-current' }, Array.from({ length: len }, (_, i) => peg(cur[i] ?? null, `mm-slot-${i}`))),
          h(
            'div.mm-palette',
            {},
            Array.from({ length: v.options.colors }, (_, c) =>
              h(
                'button.mm-pick',
                { 'data-testid': `mm-color-${c}`, 'aria-label': PEGS[c]?.name ?? '', disabled: cur.length >= len, onclick: () => set([...cur, c]) },
                peg(c),
              ),
            ),
          ),
          h(
            'div.row.actions',
            {},
            h('button.big', { 'data-testid': 'mm-undo', disabled: cur.length === 0, onclick: () => set(cur.slice(0, -1)) }, 'Undo'),
            h('button.big', { 'data-testid': 'mm-clear', disabled: cur.length === 0, onclick: () => set([]) }, 'Clear'),
            h(
              'button.primary.big',
              {
                'data-testid': 'mm-submit',
                disabled: cur.length !== len,
                onclick: () => ctx.act(setting ? { type: 'code', code: cur } : { type: 'guess', guess: cur }),
              },
              setting ? 'Lock in code' : 'Submit guess',
            ),
          ),
        )
      : null;

    const last = v.history[v.history.length - 1];
    const secretNow = !done && v.secret
      ? h('div.mm-secret', { 'data-testid': 'mm-secret' }, h('span.muted', {}, 'Your code'), h('span.mm-pegs', {}, v.secret.map((c) => peg(c))))
      : null;
    const scores =
      v.rounds > 1
        ? h(
            'div.mm-scores',
            { 'data-testid': 'mm-scores' },
            v.scores.map((p, s: Seat) => h(`span.mm-score${s === v.codemaker && !done ? '.maker' : ''}`, { 'data-testid': `mm-score-${s}` }, `${name(ctx, s)}: ${p}`)),
          )
        : null;

    return h(
      'div.mm-table',
      {},
      h('div.muted', { 'data-testid': 'mm-round' }, solo ? `${v.options.guesses} guesses` : `Round ${v.round + 1} of ${v.rounds}${v.codemaker !== null ? ` · ${name(ctx, v.codemaker)} ${v.codemaker === ctx.you ? 'are' : 'is'} the codemaker` : ''}`),
      scores,
      h('div.event', { 'data-testid': 'mm-status' }, status),
      secretNow,
      // What the key pegs mean (FR-MASTERMIND-20).
      !setting && (v.guesses.length || mine) ? h('div.mm-legend', { 'data-testid': 'mm-legend' },
        h('span', {}, h('span.mm-key.black'), 'right color, right spot'),
        h('span', {}, h('span.mm-key.white'), 'right color, wrong spot'),
        h('span', {}, h('span.mm-key'), 'no match')) : null,
      board,
      editor,
      last && (done || v.guesses.length === 0) ? revealRow(ctx, last, v.history.length) : null,
    );
  },
};
